/**
 * Member avatars. Requirement 9.9 — **never names, never text.**
 *
 * Picks render as avatars on both the phone and the television, which means every member needs a visual
 * identity whether or not they have a photograph. Google sign-in supplies one; a Cognito account created
 * with an email address supplies nothing. So one is derived from the member's subject id.
 *
 * **Colour and shape, not colour alone.** Two shapes are distinguishable to someone who cannot tell the
 * two colours apart, and a family is exactly the group most likely to contain one colour-blind person and
 * no way to mention it. Eight hues by eight shapes is sixty-four identities, which is more than a garage
 * has members.
 *
 * The derivation is duplicated in `MemberAvatar.swift` and **must stay identical**, because the whole
 * mechanism rests on recognising your own mark on the television after choosing it on your phone. A
 * 32-bit hash is used rather than the 64-bit one the vehicle placeholder colours use, precisely so the
 * two platforms can agree: JavaScript numbers lose integer precision past 2^53, so a 64-bit multiply
 * would drift from Swift's.
 */

/**
 * How many identities exist, and why it is a full hue circle rather than a palette.
 *
 * An earlier version picked from eight hand-chosen hues, which with eight shapes gave sixty-four
 * identities — and a **4.6% chance that a family of three contained two identical marks**, rising to
 * about 14% at five. That was measured, not guessed, after two of three seeded members came out as the
 * same red circle on a television. Since recognising your own mark *is* the mechanism (requirement 9.9),
 * a one-in-twenty chance of two people being indistinguishable is not a rounding error.
 *
 * Using the whole circle gives 360 x 8 = 2880 identities and drops that to 0.12% at three members and
 * 0.4% at five. The shape still carries the primary distinction, so two members landing a few degrees
 * apart are told apart by shape anyway; the only bad case left is a near-identical hue *and* the same
 * shape, which is what the arithmetic above is measuring.
 *
 * Deduplicating against the roster was the alternative and was rejected: it would mean somebody's mark
 * changing when a new member joined, and a mark that changes is worse than one that is merely unlucky.
 */
const HUE_COUNT = 360;

export type AvatarShape = "circle" | "square" | "triangle" | "diamond" | "hexagon" | "star" | "shield" | "cross";

const SHAPES: AvatarShape[] = [
  "circle",
  "square",
  "triangle",
  "diamond",
  "hexagon",
  "star",
  "shield",
  "cross",
];

/** djb2, held to 32 bits so Swift and JavaScript produce the same number. */
function hash32(value: string): number {
  let hash = 5381;
  for (let index = 0; index < value.length; index += 1) {
    // `| 0` after the multiply keeps this in int32 the way Swift's UInt32 wraps.
    hash = (Math.imul(hash, 33) + value.charCodeAt(index)) | 0;
  }
  return hash >>> 0;
}

export interface AvatarIdentity {
  hue: number;
  shape: AvatarShape;
}

export function identity(sub: string): AvatarIdentity {
  const hash = hash32(sub);
  return {
    hue: hash % HUE_COUNT,
    // A different part of the hash, so two members a few degrees apart in hue are very unlikely to also
    // share a shape — which is what keeps them distinguishable.
    shape: SHAPES[Math.floor(hash / HUE_COUNT) % SHAPES.length] ?? "circle",
  };
}

function path(shape: AvatarShape): string {
  switch (shape) {
    case "square":
      return "M22 22h56v56H22z";
    case "triangle":
      return "M50 18l34 62H16z";
    case "diamond":
      return "M50 14l36 36-36 36-36-36z";
    case "hexagon":
      return "M50 12l33 19v38L50 88 17 69V31z";
    case "star":
      return "M50 10l12 26 28 3-21 19 6 28-25-14-25 14 6-28L10 39l28-3z";
    case "shield":
      return "M50 12l32 10v28c0 20-14 32-32 38-18-6-32-18-32-38V22z";
    case "cross":
      return "M38 14h24v24h24v24H62v24H38V62H14V38h24z";
    case "circle":
    default:
      return "M50 50m-36 0a36 36 0 1 0 72 0a36 36 0 1 0-72 0";
  }
}

interface Props {
  sub: string;
  /** Google's photograph when there is one. A real face beats a generated mark. */
  picture?: string | null;
  /** For assistive technology only. Requirement 9.9 forbids showing it. */
  name?: string | null;
  size?: number;
  /** Dimmed when this member has not picked yet — requirement 9.5's sit-it-out, made visible. */
  faded?: boolean;
}

export function MemberAvatar({ sub, picture, name, size = 30, faded = false }: Props) {
  const { hue, shape } = identity(sub);
  const label = name ?? "A member";

  if (picture) {
    return (
      <img
        className={`avatar${faded ? " faded" : ""}`}
        src={picture}
        alt={label}
        width={size}
        height={size}
        loading="lazy"
      />
    );
  }

  return (
    <svg
      className={`avatar${faded ? " faded" : ""}`}
      width={size}
      height={size}
      viewBox="0 0 100 100"
      role="img"
      aria-label={label}
    >
      <circle cx="50" cy="50" r="50" fill={`hsl(${hue} 30% 22%)`} />
      <path d={path(shape)} fill={`hsl(${hue} 78% 62%)`} />
    </svg>
  );
}
